import { useEffect, useSyncExternalStore } from 'react';

/**
 * La sesión del CRM tiene dos piezas que caducan por separado:
 *
 * - La cookie de Easy Auth (la sesión de Microsoft), 8 h fijas desde que se
 *   entra. Solo viaja en los GET: con ella puesta, Easy Auth rechaza las
 *   escrituras.
 * - El token de sesión que da el servidor en /api/auth/me y que llevan las
 *   escrituras. Vive en memoria del servidor, así que cada despliegue lo borra.
 *
 * Que caducara el token con la cookie aún viva era lo que dejaba navegar y
 * luego fallar al guardar. Ahora un 401 pide token nuevo y reintenta
 * (`fetchConSesion` en api.ts), y solo si la cookie también ha caducado se
 * marca la sesión como caducada y el Layout tapa el CRM con la pantalla de
 * login.
 */

export interface UsuarioSesion {
  userId: string;
  userDetails: string;
  userRoles: string[];
  nombre?: string;
}

const CLAVE_TOKEN = 'crm_session_token';
let token: string | null = sessionStorage.getItem(CLAVE_TOKEN);

export function getSessionToken(): string | null {
  return token;
}

export function setSessionToken(nuevo: string) {
  token = nuevo;
  sessionStorage.setItem(CLAVE_TOKEN, nuevo);
}

let caducada = false;
const oyentes = new Set<() => void>();

function marcarCaducada(valor: boolean) {
  if (caducada === valor) return;
  caducada = valor;
  oyentes.forEach(o => o());
}

export function useSesionCaducada(): boolean {
  return useSyncExternalStore(
    oyente => {
      oyentes.add(oyente);
      return () => oyentes.delete(oyente);
    },
    () => caducada,
  );
}

/**
 * Pregunta al servidor si la sesión de Microsoft sigue viva y, si es así, se
 * trae un token nuevo. Lanza si el servidor no contesta bien —un despliegue a
 * medio reiniciar—, porque eso no dice nada de la sesión.
 */
export async function consultarSesion(): Promise<UsuarioSesion | null> {
  const res = await fetch('/api/auth/me', { credentials: 'include', cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const datos: { clientPrincipal: UsuarioSesion | null; sessionToken?: string } = await res.json();
  if (datos.sessionToken) setSessionToken(datos.sessionToken);
  marcarCaducada(!datos.clientPrincipal);
  return datos.clientPrincipal;
}

let enCurso: Promise<boolean> | null = null;

/**
 * `true` si la sesión sigue viva y ya hay token nuevo. Una sola consulta para
 * todos los que la pidan a la vez: tras un despliegue fallan juntas todas las
 * peticiones de la pantalla.
 */
export function renovarSesion(): Promise<boolean> {
  if (!enCurso) {
    enCurso = consultarSesion()
      .then(usuario => usuario !== null)
      .finally(() => { enCurso = null; });
  }
  return enCurso;
}

/** Easy Auth devuelve a `volverA` tras entrar. Tiene que ser una ruta relativa. */
export function urlLogin(volverA: string): string {
  return `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(volverA)}`;
}

const CADA_MS = 5 * 60 * 1000;
const MINIMO_ENTRE_CONSULTAS_MS = 60 * 1000;

/**
 * Comprueba la sesión cada 5 minutos y al volver a la pestaña, para que la
 * pantalla de login salga cuando caduca y no al darle a guardar.
 */
export function useVigilarSesion(activa: boolean) {
  useEffect(() => {
    if (!activa) return;
    let ultima = Date.now();
    const comprobar = (forzar: boolean) => {
      if (!forzar && Date.now() - ultima < MINIMO_ENTRE_CONSULTAS_MS) return;
      ultima = Date.now();
      // Sin conexión no se sabe nada de la sesión: se deja como estaba.
      renovarSesion().catch(() => {});
    };
    // Con la sesión caducada se consulta siempre: al cerrarse la ventana de
    // Microsoft el foco vuelve aquí, y es la señal de que ya se ha entrado.
    const alVolver = () => {
      if (document.visibilityState === 'visible') comprobar(caducada);
    };
    const intervalo = window.setInterval(() => comprobar(true), CADA_MS);
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      window.clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [activa]);
}
